import { isValid } from "date-fns"
import log from "loglevel"
import { TimeUtils } from "./utils/TimeUtils"

export const flattenObj = (
    currentNode: {
        [key: string]: any
    },
    target: any = null,
) => {
    const flattenInto = target ?? {}
    for (const key in currentNode) {
        const value = currentNode[key]

        if (typeof value === "object" && value != null && !Array.isArray(value)) {
            flattenObj(value, flattenInto)
        } else {
            flattenInto[key] = value
        }
    }
    return flattenInto
}

export const isEmptyOrInvalid = <T extends string | object | number | boolean | undefined | null>(value: T) => {
    // Only check against object, number, or string
    if (value == null) {
        return true
    }

    switch (typeof value) {
        case "object":
            if (Array.isArray(value)) {
                return value.length === 0
            }
            if (value instanceof Date) {
                return !isValid(value)
            }
            // Is not an array or Date object; check how many entries it has
            return Object.entries(value).length === 0
        case "string":
            return ["", "Invalid date"].includes(value)
        case "number":
            // we are allowing 0, but negative numbers are invalid
            return value < 0
        case "boolean":
            // a boolean is inherently not empty if it exists
            return false
        default:
            throw new Error(`IsEmptyOrInvalid error: Value ${value} with typeof ${typeof value} is not supported.`)
    }
}

/**
 * @Deprecated
 * TODO Replace this with an NPM library function. This has some weird results, like finding objects with equal actual
 *   properties to be not-equal.
 * Deep equality comparison between two objs or arrays.
 */
export const isEqual = (obj1: any, obj2: any) => {
    if (obj1 === obj2) {
        return true
    }

    if (typeof obj1 !== "object" || typeof obj2 !== "object" || obj1 == null || obj2 == null) {
        return false
    }

    const keysA = Object.keys(obj1)
    const keysB = Object.keys(obj2)

    if (keysA.length !== keysB.length) {
        return false
    }

    let result = true

    keysA.forEach((key) => {
        if (!keysB.includes(key)) {
            result = false
        }

        if (typeof obj1[key] === "function" || typeof obj2[key] === "function") {
            if (obj1[key].toString() !== obj2[key].toString()) {
                result = false
            }
        }

        if (!isEqual(obj1[key], obj2[key])) {
            result = false
        }
    })

    return result
}

/**
 * Returns items in arr1 that aren't exactly duplicated in arr2.
 */
export const diff = (arr1: object[], arr2: object[]) => {
    return arr1.filter((elem1) => !arr2.some((elem2) => isEqual(elem1, elem2)))
}

/**
 * Returns true if there is a difference.
 */
export const isDiff = (obj1: object, obj2: object) => {
    return JSON.stringify(obj1) !== JSON.stringify(obj2)
}

export const sortByField = (arr: any[], field: string, sortingArr: (number | string)[] | null = null) => {
    return [...arr].sort((a: any, b: any) => {
        if (sortingArr && !a[field]) {
            throw new Error("Sorting error: missing field supplied.")
        }

        //  self
        if (!sortingArr) {
            if (a[field]) {
                if (b[field]) {
                    return 0
                }
                return -1
            }
            return 1
        }
        // arr w/ objs ids for sorting

        if (sortingArr.includes(a[field])) {
            if (sortingArr.includes(b[field])) {
                return 0
            }
            return -1
        }
        return 1
    })
}

// https://rosettacode.org/wiki/Range_expansion#ES6
/**
 * Takes a string containing a range and ouputs range as list
 *
 * @param str     string containing range ex 5, 3-11, 20
 * @param unique  filter duplicates
 * @param sort    sort asc
 */
export const rangeToList = (str: string, unique = false, sort = true) => {
    // range :: Int -> Int -> Maybe Int -> [Int]
    const range = (m: any, n: any, step: any) => {
        const d = (step || 1) * (n >= m ? 1 : -1)
        return Array.from({ length: Math.floor((n - m) / d) + 1 }, (_, i) => m + i * d)
    }

    // check for invalid chars
    const regex = /[^0-9-,\s]/i
    const match = str.match(regex)
    if (str && match) {
        throw new Error(`Invalid character in range: "${match[0]}"`)
    }

    // remove whitespace around dashes
    const shakenStr = str.replace(/(\s+)?-(\s+)?/g, "-")

    // concat map yields flattened output list
    const listRange: number[] = [].concat.apply(
        [],
        shakenStr
            .split(/[\s,]+/) // split by whitespace and comma
            .map((x) => {
                const xSplit = x.split("-")

                if (xSplit.length > 2) {
                    throw new Error(`Multiple dashes in sub-range: ${xSplit.join("-")}`)
                }

                return xSplit.reduce((accumulator: any, current: string, index: number, arr: string[]) => {
                    const currentTrimmed = current.trim()

                    if (currentTrimmed.length) {
                        if (index) {
                            // item is negative if preceded by empty string (arr[index.tsx - 1].length === "")
                            const value = Number.parseInt(
                                arr[index - 1].trim().length ? currentTrimmed : `-${currentTrimmed}`,
                                10,
                            )
                            if (value < 0) {
                                throw new Error(`Negative value in range: ${value}`)
                            }
                            return accumulator.concat(value)
                        }
                        return [+currentTrimmed]
                    }
                    return accumulator
                }, [])
            })
            .map((r: any) => (r.length > 1 ? range.apply(null, r) : r)),
    )

    // remove duplicates and sort
    let result = [...listRange]
    if (unique) {
        result = [...new Set(listRange)]
    }
    if (sort) {
        ;[...result].sort((a, b) => (a > b ? 1 : -1))
    }

    return result
}

/**
 * Downloads a file using an anchor tag.
 * @param blob      file
 * @param filename
 */
export const downloadFile = (blob: Blob, filename = "nexus_default_export_name.txt") => {
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    document.body.appendChild(a) // required for firefox
    a.click()
    a.remove()
}

export const roundDecimals = (value: number, decimals: number) => {
    return Math.floor(value * 10 ** decimals) / 10 ** decimals
}

export const countDecimals = (value: number) => {
    if (Math.floor(value) === value) {
        return 0
    }
    const val = value.toString().split(".")[1]
    return val ? val.length : 0
}

export const groupBy = <T>(toGroup: T[], groupBy?: (value: T) => any): Map<T, number> => {
    const valueMap = new Map<T, number>()
    toGroup.forEach((value) => {
        const groupByValue = groupBy == null ? value : groupBy(value)
        if (!valueMap.has(groupByValue)) {
            valueMap.set(groupByValue, 0)
        }
        valueMap.set(groupByValue, valueMap.get(groupByValue)! + 1)
    })
    return valueMap
}

export const sortedByCount = <T>(toSortByCount: T[]): T[] => {
    const arrayValues = Array.from(groupBy(toSortByCount), ([value, count]) => ({ value, count }))
    return arrayValues.sort((item1, item2) => item1.count - item2.count).map((toMap) => toMap.value)
}

/**
 * Useful for debugging and dev purposes, pretty print JSON objects with a message.
 */
export const printPrettyJson = (message: string, ...objs: any[]) => {
    log.debug(`${message}\n\n${objs.map((obj, idx) => (idx > 0 ? "\n\n" : "") + prettyJson(obj))}`)
}

export const prettyJson = (obj: any) => JSON.stringify(obj, null, 2)

log.setLevel(log.levels.DEBUG)

export const snakeCaseToTitleCase = (text: string) => {
    return text
        .replace(/^[-_]*(.)/, (_, c) => c.toUpperCase()) // Initial char (after -/_)
        .replace(/[-_]+(.)/g, (_, c) => ` ${c.toUpperCase()}`)
}

export const compactFlownDate = (date?: string | null) => {
    if (isEmptyOrInvalid(date)) {
        return undefined
    }
    return TimeUtils.formatDate(date).replaceAll("-", "")
}

export const getPercentAmountOfTotal = (amount: number, total: number) => Math.round((amount / total) * 100)
